/**
 * Attaches the Firebase ID token to same-origin calls of the browser-only API routes that the
 * server now protects with requireAdmin (see server/express-api.ts). Done once, globally, so the
 * existing fetch() call sites keep working unchanged.
 */

import { auth } from '../config/firebase';

const GUARDED_PREFIXES = [
  '/api/feed/fetch',
  '/api/feed/soap',
  '/api/feed/excel-proxy',
  '/api/woo-proxy',
  '/api/sync/sessions',
  '/api/ai/',
];

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

export function installAuthFetch(): void {
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init: RequestInit = {}) => {
    let pathname: string;
    try {
      const url = new URL(requestUrl(input), window.location.origin);
      if (url.origin !== window.location.origin) return originalFetch(input, init);
      pathname = url.pathname;
    } catch {
      return originalFetch(input, init);
    }
    if (!GUARDED_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
      return originalFetch(input, init);
    }

    if (typeof auth.authStateReady === 'function') {
      await auth.authStateReady();
    }
    const user = auth.currentUser;
    if (!user) return originalFetch(input, init);

    const headers = new Headers(init.headers ?? (input instanceof Request ? input.headers : undefined));
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${await user.getIdToken()}`);
    }
    return originalFetch(input, { ...init, headers });
  };
}
